/* =========================================================
   Hairy Housemates :: Magnetic CTA + golden-hour bloom
   Cycle 259 -- canon SS6 (magnetic CTA) + SS4 (depth) + SS7 (textures)
   --
   Pointer drags primary buttons toward the cursor by 0.28 of delta,
   clamped to plus/minus 14px. Sets --ang on the element so the conic
   bloom in CSS rotates to face the pointer. Spring-back on leave via
   cubic-bezier(.34, 1.56, .64, 1) over 420ms (canon --hh-ease-spring).
   --
   Disabled on (prefers-reduced-motion: reduce) and (pointer: coarse).
   IntersectionObserver suspends listeners when the button is offscreen.
   Idempotent boot via __hhMagBound flag.
   ========================================================= */
(function (w, d) {
  'use strict';
  if (!w || !d) return;

  var SELECTOR = '.hh-btn--primary, [data-hh-magnetic]:not([data-hh-magnetic="off"])';
  var STRENGTH = 0.28;
  var MAX_PX   = 14;
  var SPRING_MS = 420;

  var reduceMQ = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var coarseMQ = w.matchMedia ? w.matchMedia('(pointer: coarse)')                 : null;

  function prefersReduce(){ return !!(reduceMQ && reduceMQ.matches); }
  function isCoarse(){ return !!(coarseMQ && coarseMQ.matches); }
  function magneticDisabled(){ return prefersReduce() || isCoarse(); }

  var bound = []; // { el, off }
  var io = null;
  var visibleSet = new WeakSet();

  function clamp(v, lo, hi){ return v < lo ? lo : v > hi ? hi : v; }

  function bindOne(el){
    if (!el || el.__hhMagBound) return;
    el.__hhMagBound = true;

    var rect = null;
    var raf  = 0;
    var pending = null;
    var springTimer = 0;

    function refreshRect(){ rect = el.getBoundingClientRect(); }

    function onEnter(){
      if (magneticDisabled()) return;
      refreshRect();
    }

    function flush(){
      raf = 0;
      if (!pending || !rect) return;
      var cx = rect.left + rect.width  / 2;
      var cy = rect.top  + rect.height / 2;
      var dx = pending.x - cx;
      var dy = pending.y - cy;
      pending = null;
      var tx = clamp(dx * STRENGTH, -MAX_PX, MAX_PX);
      var ty = clamp(dy * STRENGTH, -MAX_PX, MAX_PX);
      // atan2 returns radians from positive x-axis; rotate +90deg so the
      // bloom centers on the pointer side, not the right edge.
      var ang = Math.atan2(dy, dx) * 180 / Math.PI + 90;
      el.style.transform = 'translate3d(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px,0)';
      el.style.setProperty('--ang', ang.toFixed(1) + 'deg');
    }

    function onMove(ev){
      if (magneticDisabled()) return;
      if (!visibleSet.has(el)) return;
      if (!rect) refreshRect();
      pending = { x: ev.clientX, y: ev.clientY };
      // Cancel any in-flight spring; resume direct pointer drive.
      if (el.dataset.hhMagneticSpringing === '1'){
        el.removeAttribute('data-hh-magnetic-springing');
      }
      if (!raf) raf = w.requestAnimationFrame(flush);
    }

    function onLeave(){
      if (raf){ w.cancelAnimationFrame(raf); raf = 0; }
      pending = null;
      if (magneticDisabled()){
        el.style.transform = '';
        el.style.removeProperty('--ang');
        return;
      }
      // Flag spring transition curve via attribute so CSS swaps easing.
      el.dataset.hhMagneticSpringing = '1';
      el.style.transform = 'translate3d(0,0,0)';
      el.style.removeProperty('--ang');
      if (springTimer) w.clearTimeout(springTimer);
      springTimer = w.setTimeout(function(){
        el.removeAttribute('data-hh-magnetic-springing');
        el.style.transform = '';
        springTimer = 0;
      }, SPRING_MS + 40);
    }

    el.addEventListener('pointerenter', onEnter);
    el.addEventListener('pointermove',  onMove);
    el.addEventListener('pointerleave', onLeave);
    el.addEventListener('pointercancel', onLeave);
    el.addEventListener('blur',         onLeave, true);

    if (io) io.observe(el);

    bound.push({ el: el, off: function(){
      el.removeEventListener('pointerenter', onEnter);
      el.removeEventListener('pointermove',  onMove);
      el.removeEventListener('pointerleave', onLeave);
      el.removeEventListener('pointercancel', onLeave);
      el.removeEventListener('blur',         onLeave, true);
      if (io) try { io.unobserve(el); } catch(_e){}
      if (raf) w.cancelAnimationFrame(raf);
      if (springTimer) w.clearTimeout(springTimer);
      el.style.transform = '';
      el.style.removeProperty('--ang');
      el.removeAttribute('data-hh-magnetic-springing');
      el.__hhMagBound = false;
    }});
  }

  function unbindAll(){
    while (bound.length){
      var rec = bound.pop();
      try { rec.off(); } catch(_e){}
    }
  }

  function setupObserver(){
    if (!('IntersectionObserver' in w)) return;
    if (io){ io.disconnect(); io = null; }
    io = new IntersectionObserver(function(entries){
      entries.forEach(function(en){
        if (en.isIntersecting) visibleSet.add(en.target);
        else visibleSet.delete(en.target);
      });
    }, { threshold: 0.1 });
  }

  function boot(){
    unbindAll();
    if (magneticDisabled()){
      // Still fine to no-op; CSS guards already nuke transform/bloom.
      return;
    }
    setupObserver();
    var nodes = d.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) bindOne(nodes[i]);
  }

  // Allow late-loaded CTAs (predictive search, cart drawer, etc.) to hook in.
  function bindNew(root){
    if (magneticDisabled()) return;
    if (!io) setupObserver();
    var scope = root && root.querySelectorAll ? root : d;
    var nodes = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < nodes.length; i++) bindOne(nodes[i]);
  }

  function attachMQ(mq, fn){
    if (!mq) return;
    if (typeof mq.addEventListener === 'function') mq.addEventListener('change', fn);
    else if (typeof mq.addListener === 'function') mq.addListener(fn);
  }
  attachMQ(reduceMQ, boot);
  attachMQ(coarseMQ, boot);

  if (d.readyState !== 'loading') boot();
  else d.addEventListener('DOMContentLoaded', boot);

  w.HH = Object.assign(w.HH || {}, {
    magnetic: {
      boot:   boot,
      bindNew: bindNew,
      unbindAll: unbindAll
    }
  });
})(window, document);
